import { Fragment, useState, useEffect, useRef } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { useQuery } from '@tanstack/react-query';
import ReactECharts from 'echarts-for-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { apiHelpers } from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import { useCreditCosts } from '../../hooks/useCreditCosts';
import toast from 'react-hot-toast';
import {
  XMarkIcon,
  PaperAirplaneIcon,
  SparklesIcon,
  ArrowPathIcon,
  CheckIcon,
  ChartBarIcon,
  ChartPieIcon,
  TableCellsIcon,
  PresentationChartLineIcon,
  ArrowTrendingUpIcon,
  CircleStackIcon,
  UserIcon,
  LightBulbIcon,
  Squares2X2Icon,
  GlobeAltIcon,
  CpuChipIcon,
  Squares2X2Icon as MultiIcon,
  CodeBracketIcon,
  ChevronDownIcon,
  DocumentDuplicateIcon
} from '@heroicons/react/24/outline';
import MultiSourceSelector from '../common/MultiSourceSelector';
import ThinkingChain from '../common/ThinkingChain';

const chartTypes = [
  { id: 'bar', name: 'Bar', icon: ChartBarIcon, description: 'Compare categories' },
  { id: 'line', name: 'Line', icon: PresentationChartLineIcon, description: 'Show trends' },
  { id: 'pie', name: 'Pie', icon: ChartPieIcon, description: 'Show proportions' },
  { id: 'donut', name: 'Donut', icon: ChartPieIcon, description: 'Ring proportions' },
  { id: 'area', name: 'Area', icon: ArrowTrendingUpIcon, description: 'Volume over time' },
  { id: 'funnel', name: 'Funnel', icon: ArrowTrendingUpIcon, description: 'Conversion stages' },
  { id: 'heatmap', name: 'Heatmap', icon: Squares2X2Icon, description: 'Density patterns' },
  { id: 'cohort_table', name: 'Cohort', icon: Squares2X2Icon, description: 'Retention matrix' },
  { id: 'geo', name: 'Geo Map', icon: GlobeAltIcon, description: 'Location mapping' },
  { id: 'table', name: 'Table', icon: TableCellsIcon, description: 'Detailed data' },
  { id: 'kpi', name: 'KPI', icon: CircleStackIcon, description: 'Single metric' }
];

// Helper function to format cell values properly (handles BigQuery date objects, etc.)
function formatCellValue(value) {
  if (value === null || value === undefined) {
    return '—';
  }
  if (typeof value === 'boolean') {
    return value ? 'true' : 'false';
  }
  // Handle BigQuery date/datetime objects which come as { value: '...' }
  if (typeof value === 'object') {
    // BigQuery DATE, TIMESTAMP, DATETIME often return as { value: 'YYYY-MM-DD' }
    if (value.value !== undefined) {
      return String(value.value);
    }
    // Handle Date objects
    if (value instanceof Date) {
      return value.toISOString().split('T')[0];
    }
    // For other objects, stringify
    return JSON.stringify(value);
  }
  return String(value);
}

export default function AIChartBuilder({ isOpen, onClose, dashboardId, onChartCreated }) {
  const { user, getCurrentCompanyId } = useAuthStore();
  const { formatCost } = useCreditCosts();
  const messagesEndRef = useRef(null);
  
  // Mobile view toggle (chat vs preview)
  const [mobileTab, setMobileTab] = useState('chat');
  
  // Chat state
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Thinking chain (streaming progress)
  const [thinkingStep, setThinkingStep] = useState(0);
  const [thinkingStartTime, setThinkingStartTime] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [stepDetails, setStepDetails] = useState({});
  
  // AI Model state
  const [provider, setProvider] = useState(user?.preferences?.defaultAIProvider || 'anthropic');
  const [model, setModel] = useState('');
  
  // Chart state
  const [chartConfig, setChartConfig] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [selectedChartType, setSelectedChartType] = useState('bar');
  const [recommendedType, setRecommendedType] = useState(null);
  const [chartName, setChartName] = useState('');
  const [previewError, setPreviewError] = useState(null);
  
  // SQL explanation state
  const [showSQL, setShowSQL] = useState(false);
  const [sqlExplanation, setSqlExplanation] = useState('');
  const [isExplaining, setIsExplaining] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  
  // Data sources
  const [dataSources, setDataSources] = useState([]);
  const [views, setViews] = useState([]);
  const [relationships, setRelationships] = useState([]); // NEW: For join hints
  const [selectedDataSource, setSelectedDataSource] = useState('auto');
  const [selectedDataSources, setSelectedDataSources] = useState([]); // NEW: Multiple source selection
  const [selectedSourceType, setSelectedSourceType] = useState('auto'); // 'datasource', 'view', or 'auto'
  const [sourceMode, setSourceMode] = useState('auto'); // NEW: 'auto', 'single', or 'multi'
  const [aiSelectedSource, setAiSelectedSource] = useState(null); // Track AI's selection when auto mode
  const [aiSelectedSources, setAiSelectedSources] = useState([]); // NEW: Track multiple AI selections
  
  // Fetch available AI models
  const { data: modelsData } = useQuery({
    queryKey: ['aiModels'],
    queryFn: () => apiHelpers.getModels(),
    staleTime: 5 * 60 * 1000 // Cache for 5 minutes
  });
  
  const models = modelsData?.data?.models || [];
  const providers = modelsData?.data?.providers || {};
  
  // Set default model when provider changes or models load
  useEffect(() => {
    if (modelsData?.data?.providers?.[provider]) {
      setModel(modelsData.data.providers[provider].defaultModel);
    }
  }, [provider, modelsData]);
  
  // Fetch data sources on open
  useEffect(() => {
    if (isOpen) {
      fetchChartSources();
      // Reset state
      setMessages([]);
      setChartConfig(null);
      setChartData(null);
      setChartName('');
      setPreviewError(null);
      setRecommendedType(null);
      setAiSelectedSource(null);
      setAiSelectedSources([]);
      setSelectedDataSources([]);
      setSourceMode('auto');
      setShowSQL(false);
      setSqlExplanation('');
    }
  }, [isOpen]);
  
  // Auto-scroll messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Elapsed time while thinking
  useEffect(() => {
    if (!thinkingStartTime) return;
    const interval = setInterval(() => {
      setElapsedTime(Date.now() - thinkingStartTime);
    }, 100);
    return () => clearInterval(interval);
  }, [thinkingStartTime]);
  
  const fetchChartSources = async () => {
    try {
      const companyId = getCurrentCompanyId();
      const [sourcesResponse, relResponse] = await Promise.all([
        apiHelpers.getChartSources({ companyId }),
        apiHelpers.getRelationships({ companyId }).catch(() => ({ data: { relationships: [] } }))
      ]);
      
      const ds = sourcesResponse.data.dataSources || [];
      const v = sourcesResponse.data.views || [];
      const rels = relResponse.data.relationships || [];
      
      setDataSources(ds);
      setViews(v);
      setRelationships(rels);
      
      // Default to Auto-Select so AI picks the best source
      setSelectedDataSource('auto');
      setSelectedSourceType('auto');
      setSourceMode('auto');
    } catch (error) {
      console.error('Failed to fetch chart sources:', error);
    }
  };
  
  const handleSourceSelect = (sourceId) => {
    if (sourceId === 'auto') {
      setSelectedDataSource('auto');
      setSelectedSourceType('auto');
    } else {
      const isView = sourceId.startsWith('view_');
      setSelectedDataSource(sourceId);
      setSelectedSourceType(isView ? 'view' : 'datasource');
    }
  };
  
  const handleSend = async () => {
    if (!input.trim() || isLoading) return;
    
    const userMessage = input.trim();
    console.log('[AIChartBuilder] Sending message:', userMessage);
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);
    setPreviewError(null);
    setThinkingStep(0);
    setThinkingStartTime(Date.now());
    setElapsedTime(0);
    setStepDetails({});

    try {
      const companyId = getCurrentCompanyId();
      console.log('[AIChartBuilder] Calling API with:', { companyId, dataSourceId: selectedDataSource });

      const conversationHistory = messages.map(m => ({
        role: m.role,
        content: m.content
      }));

      const payload = {
        message: userMessage,
        companyId,
        dataSourceId: sourceMode === 'auto' ? 'auto' : (sourceMode === 'multi' ? selectedDataSources[0] : selectedDataSource),
        dataSourceIds: sourceMode === 'multi' ? selectedDataSources : [],
        currentChartType: selectedChartType,
        existingConfig: chartConfig,
        existingQuery: chartConfig?.query,
        existingChartName: chartName || chartConfig?.name,
        isEditMode: !!chartConfig,
        conversationHistory,
        provider,
        model
      };

      const response = await apiHelpers.generateChartWithAIStream(payload, (progressData) => {
        const step = progressData.step != null ? progressData.step : 0;
        setThinkingStep(step);
        if (progressData.details != null) {
          setStepDetails(prev => ({ ...prev, [step]: progressData.details }));
        }
      });

      console.log('[AIChartBuilder] Raw API response:', response);
      console.log('[AIChartBuilder] Response data:', response.data);

      const {
        response: aiResponse,
        chartSuggestion,
        recommendedChartType,
        chartData: newChartData,
        selectedSource,
        selectedSources,
        isMultiSource
      } = response.data;
      
      console.log('[AIChartBuilder] Parsed response:', {
        hasAiResponse: !!aiResponse,
        aiResponseLength: aiResponse?.length,
        hasChartSuggestion: !!chartSuggestion,
        recommendedChartType,
        chartDataLength: newChartData?.length,
        chartDataSample: newChartData?.[0],
        selectedSource,
        selectedSources,
        isMultiSource,
        fullResponseData: response.data
      });
      
      // Add AI response to messages
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: aiResponse,
        chartSuggestion
      }]);
      
      // Track AI's selected source(s) when in auto mode
      if (selectedSource) {
        console.log('[AIChartBuilder] AI selected source:', selectedSource);
        setAiSelectedSource(selectedSource);
      }
      if (selectedSources && selectedSources.length > 0) {
        console.log('[AIChartBuilder] AI selected multiple sources:', selectedSources);
        setAiSelectedSources(selectedSources);
      }
      if (isMultiSource) {
        console.log('[AIChartBuilder] AI used multi-source query');
      }
      
      // Update chart preview
      if (chartSuggestion) {
        console.log('[AIChartBuilder] Setting chartConfig:', chartSuggestion);
        setChartConfig(chartSuggestion);
        if (chartSuggestion.name) {
          setChartName(chartSuggestion.name);
        }
      }
      
      if (newChartData) {
        console.log('[AIChartBuilder] Setting chartData with', newChartData.length, 'rows');
        setChartData(newChartData);
        // Auto-switch to preview on mobile when chart data arrives
        if (window.innerWidth < 1024) {
          setMobileTab('preview');
        }
      } else {
        console.warn('[AIChartBuilder] No chartData in response!');
      }
      
      // Always update chart type when AI recommends one
      if (recommendedChartType) {
        console.log('[AIChartBuilder] Setting chart type to:', recommendedChartType);
        setRecommendedType(recommendedChartType);
        setSelectedChartType(recommendedChartType);
      }
    } catch (error) {
      console.error('[AIChartBuilder] API error:', error);
      console.error('[AIChartBuilder] Error response:', error.response);
      console.error('[AIChartBuilder] Error message:', error.message);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Sorry, I encountered an error generating the chart. Please try again or rephrase your request.',
        isError: true
      }]);
      setPreviewError(error.response?.data?.error || error.message || 'Failed to generate chart');
    } finally {
      setThinkingStep(0);
      setThinkingStartTime(null);
      setElapsedTime(0);
      setStepDetails({});
      console.log('[AIChartBuilder] Request completed');
      setIsLoading(false);
    }
  };
  
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };
  
  const handleChartTypeChange = (typeId) => {
    setSelectedChartType(typeId);
    
    // If we have a config, update its type directly (no need to add message since chart updates automatically)
    if (chartConfig) {
      setChartConfig(prev => ({ ...prev, type: typeId }));
    }
  };
  
  const handleSaveChart = async () => {
    if (!chartConfig || !chartName.trim()) {
      toast.error('Please provide a chart name');
      return;
    }
    
    setIsSaving(true);
    
    try {
      // Prepare chart config, ensuring KPI charts have valueField set
      let finalChartConfig = { ...chartConfig.config };
      
      if (selectedChartType === 'kpi' && chartData && chartData.length > 0) {
        // Determine the value field using the same fallback logic as the preview
        const valueField = finalChartConfig?.kpiConfig?.valueField 
          || finalChartConfig?.series?.[0]?.field 
          || Object.keys(chartData[0])[0];
        
        // Ensure kpiConfig exists and has valueField set
        finalChartConfig.kpiConfig = {
          ...finalChartConfig.kpiConfig,
          valueField: valueField
        };
      }
      
      const chartDataPayload = {
        name: chartName,
        type: selectedChartType,
        dashboardId,
        query: chartConfig.query,
        chartConfig: finalChartConfig,
        aiGenerated: true,
        aiPrompt: messages.find(m => m.role === 'user')?.content
      };
      
      // Determine which source to use - either user-selected or AI-selected (for auto mode)
      const effectiveSourceId = selectedDataSource === 'auto' && aiSelectedSource 
        ? aiSelectedSource.id 
        : selectedDataSource;
      const effectiveSourceType = selectedDataSource === 'auto' && aiSelectedSource
        ? aiSelectedSource.type
        : selectedSourceType;
      
      // Validate that we have a valid source ID (not 'auto')
      if (!effectiveSourceId || effectiveSourceId === 'auto') {
        toast.error('Please select a data source or wait for AI to select one');
        setIsSaving(false);
        return;
      }
      
      // Handle different source types
      if (effectiveSourceType === 'view') {
        const v = views.find(view => view._id === effectiveSourceId);
        chartDataPayload.wideViewName = effectiveSourceId.replace('view_', '');
        chartDataPayload.bigQueryViewName = v?.bigQueryViewName || `view_${effectiveSourceId.replace('view_', '').toLowerCase().replace(/\s+/g, '_')}`;
        chartDataPayload.isWideView = true;
      } else {
        chartDataPayload.dataSourceId = effectiveSourceId;
      }
      
      const response = await apiHelpers.createChart(chartDataPayload);
      
      toast.success('Chart created successfully!');
      onChartCreated?.(response.data.chart);
      onClose();
    } catch (error) {
      console.error('Failed to save chart:', error);
      console.error('Error response:', error.response?.data);
      const errorMessage = error.response?.data?.details 
        ? `Validation failed: ${error.response.data.details.map(d => d.message).join(', ')}`
        : error.response?.data?.error || 'Failed to save chart';
      toast.error(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };
  
  // Handle explaining SQL query
  const handleExplainSQL = async () => {
    if (!chartConfig?.query) {
      toast.error('No SQL query to explain');
      return;
    }
    
    setIsExplaining(true);
    setSqlExplanation('');
    
    try {
      const response = await apiHelpers.explainSQLQuery({
        query: chartConfig.query,
        provider,
        model
      });
      
      console.log('[AIChartBuilder] Explain SQL response:', {
        creditCost: response.data.creditCost,
        explanation: response.data.explanation?.substring(0, 100),
        fullResponse: response.data
      });
      
      setSqlExplanation(response.data.explanation);
      
      // Use the actual credit cost from the response
      const creditsUsed = response.data.creditCost || 3;
      const creditsText = creditsUsed === 1 ? 'credit' : 'credits';
      toast.success(`SQL explained (${creditsUsed} ${creditsText} used)`);
    } catch (error) {
      console.error('Failed to explain SQL:', error);
      const errorMessage = error.response?.data?.error || 'Failed to explain SQL query';
      toast.error(errorMessage);
    } finally {
      setIsExplaining(false);
    }
  };
  
  // Build ECharts option for preview
  const getPreviewOption = () => {
    if (!chartConfig || !chartData || chartData.length === 0) {
      return null;
    }
    
    // Table, Cohort Table, and KPI don't use ECharts - return null so they render their own preview
    if (selectedChartType === 'table' || selectedChartType === 'cohort_table' || selectedChartType === 'kpi') {
      return null;
    }
    
    const config = chartConfig.config || chartConfig;
    
    const baseOption = {
      animation: true,
      tooltip: { trigger: selectedChartType === 'pie' || selectedChartType === 'donut' ? 'item' : 'axis' },
      grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
      backgroundColor: 'transparent'
    };
    
    switch (selectedChartType) {
      case 'bar':
      case 'line':
      case 'area':
        return {
          ...baseOption,
          xAxis: {
            type: 'category',
            data: chartData.map(d => d[config.xAxis?.field || Object.keys(d)[0]]),
            axisLabel: { color: '#666' }
          },
          yAxis: {
            type: 'value',
            axisLabel: { color: '#666' }
          },
          series: [{
            name: config.series?.[0]?.name || 'Value',
            type: selectedChartType === 'area' ? 'line' : selectedChartType,
            data: chartData.map(d => d[config.series?.[0]?.field || Object.keys(d)[1]]),
            areaStyle: selectedChartType === 'area' ? { opacity: 0.3 } : undefined,
            itemStyle: { 
              color: '#6366f1',
              borderRadius: selectedChartType === 'bar' ? [4, 4, 0, 0] : undefined
            },
            smooth: selectedChartType === 'line' || selectedChartType === 'area'
          }]
        };
      
      case 'pie':
      case 'donut':
        return {
          ...baseOption,
          series: [{
            type: 'pie',
            radius: selectedChartType === 'donut' ? ['40%', '70%'] : '70%',
            data: chartData.map(d => ({
              name: d[config.xAxis?.field || Object.keys(d)[0]],
              value: d[config.series?.[0]?.field || Object.keys(d)[1]]
            })),
            label: { color: '#666' }
          }]
        };
      
      case 'heatmap': {
        const keys = Object.keys(chartData[0] || {});
        const xField = config.xAxis?.field || keys[0];
        const yField = config.yAxis?.field || keys[1];
        const valueField = config.series?.[0]?.field || keys[2] || keys[1];
        
        const xValues = [...new Set(chartData.map(d => String(d[xField])))];
        const yValues = [...new Set(chartData.map(d => String(d[yField])))];
        
        const heatmapData = chartData.map(d => {
          const xIndex = xValues.indexOf(String(d[xField]));
          const yIndex = yValues.indexOf(String(d[yField]));
          return [xIndex, yIndex, d[valueField] || 0];
        });
        
        const values = heatmapData.map(d => d[2]);
        const minValue = Math.min(...values);
        const maxValue = Math.max(...values);
        
        return {
          ...baseOption,
          xAxis: {
            type: 'category',
            data: xValues,
            splitArea: { show: true }
          },
          yAxis: {
            type: 'category',
            data: yValues,
            splitArea: { show: true }
          },
          visualMap: {
            min: minValue,
            max: maxValue,
            calculable: true,
            orient: 'vertical',
            right: '2%',
            top: 'center',
            inRange: {
              color: ['#313695', '#4575b4', '#74add1', '#abd9e9', '#ffffbf', '#fee090', '#fdae61', '#f46d43', '#d73027']
            }
          },
          series: [{
            type: 'heatmap',
            data: heatmapData,
            label: { show: heatmapData.length <= 100 }
          }]
        };
      }
      
      case 'geo': {
        // For geo, show a placeholder message
        return {
          ...baseOption,
          title: {
            text: 'Geo Map Preview',
            subtext: 'Full map will render after saving',
            left: 'center',
            top: 'center'
          }
        };
      }
      
      case 'funnel': {
        const xField = config.xAxis?.field || Object.keys(chartData[0])[0];
        const yField = config.series?.[0]?.field || Object.keys(chartData[0])[1];
        
        // Sort data by value in descending order for proper funnel visualization
        const sortedData = [...chartData].sort((a, b) => (b[yField] || 0) - (a[yField] || 0));
        
        // Color palette for funnel stages
        const funnelColors = [
          '#6366f1', '#8b5cf6', '#a855f7', '#d946ef', '#ec4899',
          '#f43f5e', '#fb7185', '#fda4af', '#fecdd3', '#ffe4e6'
        ];
        
        return {
          ...baseOption,
          tooltip: {
            trigger: 'item',
            formatter: '{b}: {c}'
          },
          legend: {
            orient: 'vertical',
            right: 10,
            top: 'center',
            data: sortedData.map(d => String(d[xField]))
          },
          series: [{
            name: config.series?.[0]?.name || 'Funnel',
            type: 'funnel',
            left: '10%',
            top: 40,
            bottom: 40,
            width: '60%',
            min: 0,
            max: Math.max(...sortedData.map(d => d[yField] || 0)),
            minSize: '0%',
            maxSize: '100%',
            sort: 'descending',
            gap: 2,
            label: {
              show: true,
              position: 'inside',
              formatter: '{b}'
            },
            labelLine: {
              show: false
            },
            itemStyle: {
              borderColor: '#fff',
              borderWidth: 1
            },
            emphasis: {
              label: {
                fontSize: 14
              }
            },
            data: sortedData.map((d, index) => ({
              name: String(d[xField]),
              value: d[yField] || 0,
              itemStyle: {
                color: funnelColors[index % funnelColors.length]
              }
            }))
          }]
        };
      }
      
      default:
        return null;
    }
  };
  
  // Find selected source from data sources or views
  const selectedDs = selectedSourceType === 'view'
    ? views.find(v => v._id === selectedDataSource)
    : dataSources.find(ds => ds._id === selectedDataSource);
  const previewOption = getPreviewOption();
  
  // Debug logging for preview (only when data changes)
  useEffect(() => {
    if (chartData || chartConfig) {
      console.log('[AIChartBuilder] Preview state changed:', {
        selectedChartType,
        hasChartConfig: !!chartConfig,
        hasChartData: !!chartData,
        chartDataLength: chartData?.length,
        hasPreviewOption: !!previewOption,
        previewError
      });
    }
  }, [selectedChartType, chartConfig, chartData, previewOption, previewError]);
  
  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-hidden safe-modal-height">
          <div className="flex h-full items-stretch justify-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-7xl transform overflow-hidden bg-white dark:bg-surface-900 shadow-2xl transition-all flex flex-col h-full">
                {/* Header */}
                <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b border-surface-200 dark:border-surface-800 bg-gradient-to-r from-primary-600 to-accent-600">
                  <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                      <SparklesIcon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                    </div>
                    <div className="min-w-0">
                      <Dialog.Title className="text-base sm:text-lg font-display font-semibold text-white truncate">
                        AI Chart Builder
                        {formatCost('chart_builder') && (
                          <span className="ml-2 text-sm font-normal text-white/70 hidden sm:inline">
                            {formatCost('chart_builder')}
                          </span>
                        )}
                      </Dialog.Title>
                      <p className="text-xs sm:text-sm text-white/70 hidden sm:block">
                        Describe what you want to visualize
                      </p>
                    </div>
                  </div>
                  <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10 transition-colors flex-shrink-0">
                    <XMarkIcon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                  </button>
                </div>
                
                {/* Mobile Tab Toggle - only visible on small screens */}
                <div className="flex lg:hidden border-b border-surface-200 dark:border-surface-800">
                  <button
                    onClick={() => setMobileTab('chat')}
                    className={`flex-1 px-4 py-2.5 text-sm font-medium text-center transition-colors ${
                      mobileTab === 'chat'
                        ? 'text-primary-600 dark:text-primary-400 border-b-2 border-primary-500 bg-primary-50/50 dark:bg-primary-900/20'
                        : 'text-surface-500 hover:text-surface-700 dark:hover:text-surface-300'
                    }`}
                  >
                    Chat
                  </button>
                  <button
                    onClick={() => setMobileTab('preview')}
                    className={`flex-1 px-4 py-2.5 text-sm font-medium text-center transition-colors ${
                      mobileTab === 'preview'
                        ? 'text-primary-600 dark:text-primary-400 border-b-2 border-primary-500 bg-primary-50/50 dark:bg-primary-900/20'
                        : 'text-surface-500 hover:text-surface-700 dark:hover:text-surface-300'
                    }`}
                  >
                    Preview {chartConfig ? '●' : ''}
                  </button>
                </div>
                
                {/* Main content - Split View (stacked on mobile, side-by-side on desktop) */}
                <div className="flex-1 flex flex-col lg:flex-row overflow-hidden min-h-0">
                  {/* Left side - Chart Preview (hidden on mobile when chat tab is active) */}
                  <div className={`${mobileTab === 'preview' ? 'flex' : 'hidden'} lg:flex w-full lg:w-1/2 flex-1 min-h-0 flex-col border-b lg:border-b-0 lg:border-r border-surface-200 dark:border-surface-800 overflow-hidden`}>
                    {/* Chart Type Selector */}
                    <div className="p-3 sm:p-4 border-b border-surface-200 dark:border-surface-800 bg-surface-50 dark:bg-surface-800/50">
                      <div className="flex items-center justify-between mb-2 sm:mb-3">
                        <label className="text-sm font-medium text-surface-700 dark:text-surface-300">
                          Chart Type
                        </label>
                        {recommendedType && (
                          <div className="flex items-center gap-1 text-xs text-primary-600 dark:text-primary-400">
                            <LightBulbIcon className="w-4 h-4" />
                            AI recommends: <span className="font-medium capitalize">{recommendedType}</span>
                          </div>
                        )}
                      </div>
                      <div className="flex gap-1.5 sm:gap-2 flex-wrap">
                        {chartTypes.map(type => {
                          const Icon = type.icon;
                          const isRecommended = type.id === recommendedType;
                          const isSelected = type.id === selectedChartType;
                          
                          return (
                            <button
                              key={type.id}
                              onClick={() => handleChartTypeChange(type.id)}
                              className={`flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg border-2 transition-all text-xs sm:text-sm ${
                                isSelected
                                  ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                                  : isRecommended
                                    ? 'border-primary-300 bg-primary-25 dark:bg-primary-900/10 text-surface-700 dark:text-surface-300'
                                    : 'border-surface-200 dark:border-surface-700 text-surface-600 dark:text-surface-400 hover:border-surface-400'
                              }`}
                              title={type.description}
                            >
                              <Icon className="w-4 h-4" />
                              {type.name}
                              {isRecommended && !isSelected && (
                                <span className="w-2 h-2 rounded-full bg-primary-500" />
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    
                    {/* Data Source Selector */}
                    <div className="p-3 sm:p-4 border-b border-surface-200 dark:border-surface-800">
                      <div className="flex items-center justify-between mb-2 gap-2">
                        <label className="text-sm font-medium text-surface-700 dark:text-surface-300">
                          Data Source
                        </label>
                        {/* Mode Toggle */}
                        <div className="flex items-center gap-1 bg-surface-100 dark:bg-surface-800 rounded-lg p-0.5">
                          <button
                            onClick={() => { setSourceMode('auto'); setSelectedDataSource('auto'); setSelectedDataSources([]); }}
                            className={`px-2 py-1 text-xs rounded transition-colors ${
                              sourceMode === 'auto' 
                                ? 'bg-primary-500 text-white' 
                                : 'text-surface-600 dark:text-surface-400 hover:bg-surface-200'
                            }`}
                          >
                            ✨ Auto
                          </button>
                          <button
                            onClick={() => { setSourceMode('single'); setSelectedDataSource(''); setSelectedDataSources([]); }}
                            className={`px-2 py-1 text-xs rounded transition-colors ${
                              sourceMode === 'single' 
                                ? 'bg-primary-500 text-white' 
                                : 'text-surface-600 dark:text-surface-400 hover:bg-surface-200'
                            }`}
                          >
                            Single
                          </button>
                          <button
                            onClick={() => { setSourceMode('multi'); setSelectedDataSource(''); setSelectedDataSources([]); }}
                            className={`px-2 py-1 text-xs rounded transition-colors ${
                              sourceMode === 'multi' 
                                ? 'bg-primary-500 text-white' 
                                : 'text-surface-600 dark:text-surface-400 hover:bg-surface-200'
                            }`}
                          >
                            Multi
                          </button>
                        </div>
                      </div>
                      
                      {sourceMode === 'auto' ? (
                        <div className="p-3 bg-gradient-to-r from-primary-50 to-purple-50 dark:from-primary-900/20 dark:to-purple-900/20 rounded-lg border border-primary-200 dark:border-primary-800">
                          <div className="flex items-center gap-2">
                            <SparklesIcon className="w-5 h-5 text-primary-500" />
                            <div>
                              <p className="text-sm font-medium text-primary-700 dark:text-primary-300">
                                AI Auto-Select
                              </p>
                              <p className="text-xs text-primary-600 dark:text-primary-400">
                                AI will analyze your request and pick the best source(s), including multi-table joins if needed.
                              </p>
                            </div>
                          </div>
                          {(aiSelectedSource || aiSelectedSources.length > 0) && (
                            <div className="mt-2 pt-2 border-t border-primary-200 dark:border-primary-700">
                              <p className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1">
                                <CheckIcon className="w-3 h-3" />
                                AI selected: 
                                <span className="font-medium">
                                  {aiSelectedSources.length > 1 
                                    ? `${aiSelectedSources.length} sources (${aiSelectedSources.map(s => s.name).join(', ')})`
                                    : aiSelectedSource?.name
                                  }
                                </span>
                              </p>
                            </div>
                          )}
                        </div>
                      ) : (
                        <MultiSourceSelector
                          dataSources={dataSources}
                          views={views}
                          relationships={relationships}
                          selected={sourceMode === 'multi' ? selectedDataSources : (selectedDataSource ? [selectedDataSource] : [])}
                          onChange={(sources) => {
                            if (sourceMode === 'multi') {
                              setSelectedDataSources(sources);
                            } else {
                              setSelectedDataSource(sources[0] || '');
                              const id = sources[0];
                              if (id) {
                                const isView = id.startsWith('view_');
                                setSelectedSourceType(isView ? 'view' : 'datasource');
                              }
                            }
                          }}
                          mode={sourceMode}
                          showRelationships={sourceMode === 'multi'}
                        />
                      )}
                    </div>
                    
                    {/* Chart Preview */}
                    <div className="flex-1 p-3 sm:p-6 flex flex-col min-h-0 overflow-hidden">
                      <div className="flex items-center justify-between mb-4 flex-shrink-0">
                        <h3 className="font-medium text-surface-900 dark:text-white">
                          Preview
                        </h3>
                        {chartConfig && (
                          <button
                            onClick={() => setInput('Refresh the chart with latest data')}
                            className="btn-ghost btn-sm"
                          >
                            <ArrowPathIcon className="w-4 h-4 mr-1" />
                            Refresh
                          </button>
                        )}
                      </div>
                      
                      <div className="flex-1 min-h-0 rounded-xl border border-surface-200 dark:border-surface-700 bg-surface-50 dark:bg-surface-800/50 overflow-hidden">
                        {!chartConfig && !chartData ? (
                          <div className="h-full flex flex-col items-center justify-center text-center p-8">
                            <div className="w-16 h-16 rounded-full bg-surface-100 dark:bg-surface-800 flex items-center justify-center mb-4">
                              <ChartBarIcon className="w-8 h-8 text-surface-400" />
                            </div>
                            <h4 className="font-medium text-surface-900 dark:text-white mb-2">
                              No chart yet
                            </h4>
                            <p className="text-sm text-surface-500 max-w-xs">
                              Describe what you want to see in the chat, and I'll create a chart for you.
                            </p>
                          </div>
                        ) : previewError ? (
                          <div className="h-full flex flex-col items-center justify-center text-center p-8">
                            <div className="w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mb-4">
                              <XMarkIcon className="w-8 h-8 text-red-500" />
                            </div>
                            <h4 className="font-medium text-red-600 dark:text-red-400 mb-2">
                              Preview Error
                            </h4>
                            <p className="text-sm text-surface-500 max-w-xs">
                              {previewError}
                            </p>
                          </div>
                        ) : selectedChartType === 'table' && chartData && chartData.length > 0 ? (
                          <div className="h-full overflow-auto">
                            <table className="w-full text-sm">
                              <thead className="bg-surface-100 dark:bg-surface-700 sticky top-0">
                                <tr>
                                  {chartData[0] && Object.keys(chartData[0]).map(key => (
                                    <th key={key} className="px-4 py-2 text-left font-medium text-surface-600 dark:text-surface-400">
                                      {key}
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {chartData.slice(0, 50).map((row, i) => (
                                  <tr key={i} className="border-b border-surface-100 dark:border-surface-700 hover:bg-surface-50 dark:hover:bg-surface-700/50">
                                    {Object.values(row).map((value, j) => (
                                      <td key={j} className="px-4 py-2 text-surface-700 dark:text-surface-300">
                                        {formatCellValue(value)}
                                      </td>
                                    ))}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                            {chartData.length > 50 && (
                              <div className="px-4 py-2 text-center text-xs text-surface-500 bg-surface-50 dark:bg-surface-800 border-t border-surface-200 dark:border-surface-700">
                                Showing 50 of {chartData.length} rows
                              </div>
                            )}
                          </div>
                        ) : selectedChartType === 'cohort_table' && chartData && chartData.length > 0 ? (
                          <div className="h-full overflow-auto">
                            {(() => {
                              // Detect columns for cohort table pivot
                              const keys = Object.keys(chartData[0] || {});
                              const cohortField = keys.find(k => 
                                k.toLowerCase().includes('cohort') || 
                                (k.toLowerCase().includes('month') && !k.toLowerCase().includes('period'))
                              ) || keys[0];
                              const periodField = keys.find(k => 
                                k.toLowerCase().includes('period') || 
                                k.toLowerCase().includes('age') ||
                                k.toLowerCase() === 'm'
                              ) || keys[1];
                              const valueField = keys.find(k => 
                                k.toLowerCase().includes('value') || 
                                k.toLowerCase().includes('users') ||
                                k.toLowerCase().includes('count') ||
                                k.toLowerCase().includes('retention') ||
                                k.toLowerCase().includes('rate')
                              ) || keys[2] || keys[1];

                              // Get unique cohorts and periods
                              const cohorts = [...new Set(chartData.map(row => row[cohortField]))].sort();
                              const periods = [...new Set(chartData.map(row => Number(row[periodField])))].sort((a, b) => a - b);

                              // Build pivot map
                              const pivotMap = {};
                              chartData.forEach(row => {
                                const cohort = row[cohortField];
                                const period = Number(row[periodField]);
                                if (!pivotMap[cohort]) pivotMap[cohort] = {};
                                pivotMap[cohort][period] = row[valueField];
                              });

                              // Get cell style with color intensity
                              const getCellStyle = (value, cohort) => {
                                if (value === undefined || value === null) return {};
                                const baseValue = pivotMap[cohort]?.[0] || 1;
                                const intensity = baseValue > 0 ? Math.min(value / baseValue, 1) : 0;
                                return {
                                  backgroundColor: `rgba(99, 102, 241, ${intensity * 0.5})`,
                                  color: intensity > 0.6 ? 'white' : 'inherit'
                                };
                              };

                              return (
                                <table className="w-full text-sm border-collapse">
                                  <thead className="bg-surface-100 dark:bg-surface-700 sticky top-0">
                                    <tr>
                                      <th className="px-3 py-2 text-left font-semibold text-surface-700 dark:text-surface-300 border-b border-r border-surface-200 dark:border-surface-600 bg-surface-100 dark:bg-surface-700">
                                        Cohort
                                      </th>
                                      {periods.map(period => (
                                        <th key={period} className="px-3 py-2 text-center font-semibold text-surface-700 dark:text-surface-300 border-b border-surface-200 dark:border-surface-600 bg-surface-100 dark:bg-surface-700 min-w-[50px]">
                                          M{period}
                                        </th>
                                      ))}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {cohorts.map((cohort) => (
                                      <tr key={cohort} className="border-b border-surface-100 dark:border-surface-700">
                                        <td className="px-3 py-2 font-medium text-surface-900 dark:text-white border-r border-surface-200 dark:border-surface-700 bg-surface-50 dark:bg-surface-800/50 whitespace-nowrap">
                                          {formatCellValue(cohort)}
                                        </td>
                                        {periods.map(period => {
                                          const value = pivotMap[cohort]?.[period];
                                          const cellStyle = getCellStyle(value, cohort);
                                          return (
                                            <td key={period} className="px-3 py-2 text-center" style={cellStyle}>
                                              {value !== undefined ? (typeof value === 'number' ? value.toLocaleString() : formatCellValue(value)) : '—'}
                                            </td>
                                          );
                                        })}
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              );
                            })()}
                          </div>
                        ) : selectedChartType === 'kpi' && chartData && chartData.length > 0 ? (
                          <div className="h-full flex flex-col items-center justify-center text-center p-8">
                            <div className="text-5xl font-display font-bold text-surface-900 dark:text-white mb-2">
                              {(() => {
                                const config = chartConfig?.config || chartConfig;
                                const valueField = config?.kpiConfig?.valueField || config?.series?.[0]?.field || Object.keys(chartData[0])[0];
                                const value = chartData[0]?.[valueField];
                                const prefix = config?.kpiConfig?.prefix || '';
                                const suffix = config?.kpiConfig?.suffix || '';
                                const formattedValue = typeof value === 'number' 
                                  ? new Intl.NumberFormat().format(value)
                                  : value;
                                return `${prefix}${formattedValue}${suffix}`;
                              })()}
                            </div>
                            <p className="text-surface-500">
                              {(() => {
                                const config = chartConfig?.config || chartConfig;
                                return config?.kpiConfig?.label || config?.name || Object.keys(chartData[0])[0];
                              })()}
                            </p>
                          </div>
                        ) : previewOption ? (
                          <div className="h-full p-4">
                            <ReactECharts
                              option={previewOption}
                              style={{ height: '100%', width: '100%' }}
                              opts={{ renderer: 'svg' }}
                            />
                          </div>
                        ) : chartConfig && chartData ? (
                          <div className="h-full flex flex-col items-center justify-center text-center p-8">
                            <div className="w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center mb-3">
                              <TableCellsIcon className="w-6 h-6 text-amber-600 dark:text-amber-400" />
                            </div>
                            <p className="text-sm text-surface-500">
                              Preview not available for this chart type
                            </p>
                          </div>
                        ) : (
                          <div className="h-full flex items-center justify-center">
                            <div className="w-8 h-8 border-4 border-primary-500/30 border-t-primary-500 rounded-full animate-spin" />
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* SQL Query Section */}
                    {chartConfig && chartConfig.query && chartData && (
                      <div className="border-t border-surface-200 dark:border-surface-800">
                        <button
                          onClick={() => setShowSQL(!showSQL)}
                          className="w-full px-4 py-3 flex items-center justify-between hover:bg-surface-50 dark:hover:bg-surface-800/50 transition-colors text-left"
                        >
                          <div className="flex items-center gap-2">
                            <CodeBracketIcon className="w-4 h-4 text-surface-500 dark:text-surface-400" />
                            <span className="text-sm font-medium text-surface-700 dark:text-surface-300">
                              View SQL Query
                            </span>
                            {showSQL && (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300">
                                Advanced
                              </span>
                            )}
                          </div>
                          <ChevronDownIcon 
                            className={`w-4 h-4 text-surface-400 transition-transform ${showSQL ? 'rotate-180' : ''}`} 
                          />
                        </button>
                        
                        {showSQL && (
                          <div className="px-4 py-3 bg-surface-50 dark:bg-surface-900/50 space-y-3">
                            <div className="flex items-center justify-between mb-2">
                              <label className="text-xs font-medium text-surface-500 dark:text-surface-400 uppercase tracking-wider">
                                SQL Query
                              </label>
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => {
                                    navigator.clipboard.writeText(chartConfig.query);
                                    toast.success('SQL copied to clipboard!');
                                  }}
                                  className="text-xs text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 flex items-center gap-1 transition-colors"
                                >
                                  <DocumentDuplicateIcon className="w-3.5 h-3.5" />
                                  Copy
                                </button>
                              </div>
                            </div>
                            
                            <div className="bg-surface-900 dark:bg-black rounded-lg p-3 overflow-x-auto">
                              <pre className="text-xs font-mono text-green-400 whitespace-pre-wrap">
                                {chartConfig.query}
                              </pre>
                            </div>
                            
                            {/* Explain Query Button */}
                            <div className="flex items-start gap-2 pt-2">
                              <button
                                onClick={handleExplainSQL}
                                disabled={isExplaining}
                                className="btn-ghost btn-sm text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20"
                              >
                                {isExplaining ? (
                                  <>
                                    <ArrowPathIcon className="w-4 h-4 animate-spin mr-1" />
                                    Explaining...
                                  </>
                                ) : (
                                  <>
                                    <SparklesIcon className="w-4 h-4 mr-1" />
                                    Explain Query
                                    {formatCost('explain_sql_query') && (
                                      <span className="ml-1 text-xs opacity-75">
                                        {formatCost('explain_sql_query')}
                                      </span>
                                    )}
                                  </>
                                )}
                              </button>
                            </div>
                            
                            {/* SQL Explanation */}
                            {sqlExplanation && (
                              <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg max-h-96 overflow-y-auto">
                                <div className="flex items-start gap-2 mb-2">
                                  <SparklesIcon className="w-4 h-4 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                                  <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-100">
                                    Query Explanation
                                  </h4>
                                </div>
                                <div className="text-sm text-blue-800 dark:text-blue-200 prose prose-sm prose-blue dark:prose-invert max-w-none">
                                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                    {sqlExplanation}
                                  </ReactMarkdown>
                                </div>
                              </div>
                            )}
                            
                            {/* Info tooltip */}
                            <div className="text-xs text-surface-600 dark:text-surface-400 flex items-start gap-1 mt-2">
                              <span className="text-blue-500">ℹ️</span>
                              <span>This SQL query is executed against your data warehouse to generate the chart data.</span>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Save Section */}
                    {chartConfig && chartData && (
                      <div className="p-3 sm:p-4 border-t border-surface-200 dark:border-surface-800 bg-surface-50 dark:bg-surface-800/50">
                        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
                          <input
                            type="text"
                            value={chartName}
                            onChange={(e) => setChartName(e.target.value)}
                            placeholder="Enter chart name..."
                            className="input flex-1"
                          />
                          <button
                            onClick={handleSaveChart}
                            disabled={isSaving || !chartName.trim()}
                            className="btn-primary"
                          >
                            {isSaving ? (
                              <>
                                <ArrowPathIcon className="w-5 h-5 animate-spin mr-2" />
                                Saving...
                              </>
                            ) : (
                              <>
                                <CheckIcon className="w-5 h-5 mr-2" />
                                Save Chart
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Right side - AI Chat (hidden on mobile when preview tab is active) */}
                  <div className={`${mobileTab === 'chat' ? 'flex' : 'hidden'} lg:flex w-full lg:w-1/2 flex-1 min-h-0 flex-col overflow-hidden`}>
                    {/* Hidden AI Model selectors - keeping state for backend compatibility */}
                    <div className="hidden">
                      <select
                        value={provider}
                        onChange={(e) => setProvider(e.target.value)}
                      >
                        {Object.entries(providers).map(([key, value]) => (
                          <option key={key} value={key}>{value.name}</option>
                        ))}
                      </select>
                      <select
                        value={model}
                        onChange={(e) => setModel(e.target.value)}
                      >
                        {models
                          .filter(m => m.provider === provider)
                          .map(m => (
                            <option key={m.id} value={m.id}>
                              {m.name}
                            </option>
                          ))}
                      </select>
                    </div>
                    
                    {/* Chat Messages */}
                    <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-4">
                      {messages.length === 0 ? (
                        <div className="h-full flex flex-col items-center justify-center text-center">
                          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center mb-4">
                            <SparklesIcon className="w-8 h-8 text-white" />
                          </div>
                          <h3 className="text-lg font-display font-bold text-surface-900 dark:text-white mb-2">
                            What would you like to visualize?
                          </h3>
                          <p className="text-surface-500 max-w-sm mb-6">
                            Tell me what data you want to see, and I'll create the perfect chart for you.
                          </p>
                          
                          {/* Suggestion chips */}
                          <div className="flex flex-wrap gap-2 justify-center max-w-md">
                            {[
                              'Show sales by month',
                              'Top 10 products by revenue',
                              'Customer growth over time',
                              'Distribution of order values'
                            ].map((suggestion, i) => (
                              <button
                                key={i}
                                onClick={() => setInput(suggestion)}
                                className="px-3 py-2 rounded-full bg-surface-100 dark:bg-surface-800 text-sm text-surface-600 dark:text-surface-400 hover:bg-primary-50 hover:text-primary-600 dark:hover:bg-primary-900/30 dark:hover:text-primary-400 transition-colors"
                              >
                                {suggestion}
                              </button>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <>
                          {messages.map((msg, i) => (
                            <div key={i} className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                                msg.role === 'assistant' 
                                  ? 'bg-gradient-to-br from-primary-500 to-accent-500' 
                                  : 'bg-surface-200 dark:bg-surface-700'
                              }`}>
                                {msg.role === 'assistant' 
                                  ? <SparklesIcon className="w-4 h-4 text-white" />
                                  : <UserIcon className="w-4 h-4 text-surface-600 dark:text-surface-400" />
                                }
                              </div>
                              
                              <div className={`max-w-[85%] ${msg.role === 'user' ? 'text-right' : ''}`}>
                                <div className={`inline-block px-4 py-3 rounded-2xl ${
                                  msg.role === 'assistant'
                                    ? msg.isError 
                                      ? 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                                      : 'bg-surface-100 dark:bg-surface-800 text-surface-900 dark:text-white'
                                    : 'bg-primary-600 text-white'
                                }`}>
                                  {msg.role === 'assistant' ? (
                                    <div className="prose prose-sm dark:prose-invert max-w-none">
                                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                        {msg.content}
                                      </ReactMarkdown>
                                    </div>
                                  ) : (
                                    <p className="whitespace-pre-wrap">{msg.content}</p>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                          
                          {isLoading && (
                            <ThinkingChain
                              currentStep={Math.min(thinkingStep, 4)}
                              elapsedTime={elapsedTime}
                              stepDetails={stepDetails}
                            />
                          )}
                        </>
                      )}
                      <div ref={messagesEndRef} />
                    </div>
                    
                    {/* Chat Input */}
                    <div className="p-3 sm:p-4 border-t border-surface-200 dark:border-surface-800 bg-surface-50 dark:bg-surface-800/50">
                      <div className="flex gap-2 sm:gap-3">
                        <textarea
                          value={input}
                          onChange={(e) => setInput(e.target.value)}
                          onKeyDown={handleKeyDown}
                          placeholder="Describe what you want to visualize..."
                          rows={2}
                          className="flex-1 input resize-none text-base"
                        />
                        <button
                          onClick={handleSend}
                          disabled={!input.trim() || isLoading}
                          className="btn-primary self-end px-3 sm:px-4"
                          title={`Send message ${formatCost('chart_builder')}`}
                        >
                          <PaperAirplaneIcon className="w-5 h-5" />
                          {formatCost('chart_builder') && (
                            <span className="ml-1 text-xs opacity-75 hidden sm:inline">{formatCost('chart_builder')}</span>
                          )}
                        </button>
                      </div>
                      <p className="text-xs text-surface-500 mt-2 hidden sm:block">
                        Press Enter to send, Shift+Enter for new line
                      </p>
                    </div>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

